// ====================================================
// src/components/block/liveStreamManagment/videoEffects/avatarModelStore.ts
//
// Les modèles 3D de l'effet "Avatar" n'existent plus en dur : chaque créateur
// téléverse les siens (.glb), rangés côté serveur dans son workingSpace
// (cf. AvatarModelController, admin-api). Ce module est le pont :
//   - il retient la chaîne de la régie courante (le serveur en déduit le créateur) ;
//   - il charge la liste des modèles et la déverse dans le registre AVATAR_MODELS
//     (AvatarModels.ts), que le renderer consulte à chaque image ;
//   - il porte l'envoi et la suppression.
//
// Aucun modèle au départ : tant que la liste n'est pas arrivée (ou si le créateur n'en
// a pas), le registre est vide et l'effet n'affiche rien.
// ====================================================

import { API_URLS } from '../../../../backendServices/urls';
import { AvatarModelDef, AvatarAnchorKind, setAvatarModels } from './AvatarModels';

export type AvatarModelFamily = 'face' | 'hand' | 'decoration';

/** Une fiche telle que le serveur la renvoie. */
interface AvatarModelRecord {
  id    : string;
  label : string;
  family: AvatarModelFamily;
  anchor: AvatarAnchorKind;
  /** Côté de la personne, pour une main ; absent des modèles envoyés avant la distinction. */
  side?: 'left' | 'right' | null;
  size  : number;
}

let channelUuid: string | null = null;
let loaded = false;
const listeners = new Set<() => void>();
// Version incrémentée à chaque changement du registre — sert de "snapshot" à useAvatarModels.
let version = 0;

function notify(): void {
  version++;
  listeners.forEach(l => l());
}

function fileUrl(id: string): string {
  return `${API_URLS.avatarModelFile.replace('{id}', id)}?channelUuid=${encodeURIComponent(channelUuid ?? '')}`;
}

function toDef(r: AvatarModelRecord): AvatarModelDef {
  const isDecoration = r.family === 'decoration';
  return {
    id: r.id,
    label: r.label,
    icon: 'generic',
    // Taille visée par défaut ; le réglage "Taille" (visage/mains) et la calibration
    // (décorations) servent à l'affiner — le maillage lui-même est mesuré au chargement.
    targetRatio: r.anchor === 'hand' ? 1.1 : r.family === 'face' ? 2.4 : 1.6,
    anchor: r.anchor,
    handSide: r.family === 'hand' ? (r.side ?? undefined) : undefined,
    isDecoration: isDecoration || undefined,
    // Une tête entière remplace le visage : sa face avant se pose sur le vrai visage
    // (cf. AvatarModelDef.depthAnchor).
    depthAnchor: r.family === 'face' ? 'front' : undefined,
    url: fileUrl(r.id),
  };
}

/** Indique à quelle chaîne appartient la régie courante, et (re)charge les modèles
 * de son créateur quand elle change. Sans effet si la chaîne est inchangée. */
export function setAvatarModelsChannel(uuid: string | null): void {
  const next = uuid?.trim() || null;
  if (next === channelUuid && loaded) return;
  channelUuid = next;
  loaded = false;
  if (!next) {
    setAvatarModels([]);
    notify();
    return;
  }
  void refreshAvatarModels();
}

export async function refreshAvatarModels(): Promise<void> {
  const uuid = channelUuid;
  if (!uuid) return;
  try {
    const response = await fetch(`${API_URLS.avatarModels}?channelUuid=${encodeURIComponent(uuid)}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const records = (await response.json()) as AvatarModelRecord[];
    if (uuid !== channelUuid) return; // la régie a changé de chaîne entre-temps
    setAvatarModels(records.map(toDef));
    loaded = true;
    notify();
  } catch (e) {
    console.warn('[AVATAR] liste des modèles indisponible', e);
  }
}

export async function uploadAvatarModel(
  file: File, label: string, family: AvatarModelFamily, anchor: AvatarAnchorKind, side?: 'left' | 'right',
): Promise<void> {
  if (!channelUuid) throw new Error('Aucune chaîne');
  const form = new FormData();
  form.append('channelUuid', channelUuid);
  form.append('label', label);
  form.append('family', family);
  form.append('anchor', anchor);
  if (side) form.append('side', side);
  form.append('file', file);
  const response = await fetch(API_URLS.avatarModels, { method: 'POST', body: form });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `Erreur ${response.status}`);
  }
  await refreshAvatarModels();
}

export async function deleteAvatarModel(id: string): Promise<void> {
  if (!channelUuid) throw new Error('Aucune chaîne');
  const url = `${API_URLS.avatarModelDelete.replace('{id}', id)}?channelUuid=${encodeURIComponent(channelUuid)}`;
  const response = await fetch(url, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `Erreur ${response.status}`);
  }
  await refreshAvatarModels();
}

/** Pour React : se réabonne au registre (useSyncExternalStore). */
export function subscribeAvatarModels(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function getAvatarModelsVersion(): number {
  return version;
}
